import type { DrillVesselAnalytics } from '@felix/domain';
import { Button, SegmentedControl, Select } from '@mantine/core';
import {
  IconAlertTriangle,
  IconChartPie,
  IconClockExclamation,
  IconFileSpreadsheet,
  IconFlag,
  IconStopwatch,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { axisMax } from '../fuel/OverviewPanel.js';
import { errorCode } from '../personnel/source.js';
import table from '../pms/Pms.module.css';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { downloadFile, XLSX_TYPE } from '../reports/xlsx.js';
import { analyticsFileName, analyticsWorkbook } from './analytics-export.js';
import type { DrillAnalyticsData, DrillAnalyticsSource } from './analytics-types.js';
import classes from './DrillAnalytics.module.css';
import planner from './DrillPlanner.module.css';
import { formatSeconds } from './record-view.js';

export interface DrillAnalyticsScreenProps {
  source: DrillAnalyticsSource;
}

const isDay = (value: string | null) => (value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null);
const time = (seconds: number | null) => (seconds === null ? '—' : formatSeconds(seconds));
const rateTone = (percent: number | null) =>
  percent === null ? 'default' : percent >= 90 ? 'success' : percent >= 70 ? 'warning' : 'danger';

/**
 * Operations ▸ Drills ▸ Analytics (ortağın belgesi §9 "Fleet Analytics"; ADR 0128 Faz 5 Ek):
 * yalnız ofiste. Gemi bazında tamamlanma oranı, gecikme, ortalama toplanma ve tepki süresi
 * eğilimi, açık bulgu ve tür dağılımı; süzgeç dönem, gemi ve tür (adreste); Excel dışa aktarım.
 * Sayılar sunucuda `drillAnalytics` ile hesaplanır; ekran yalnız çizer.
 */
export function DrillAnalyticsScreen({ source }: DrillAnalyticsScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillReport.analytics.${key}`, values);
  const [query, setQuery] = usePmsQuery();
  const from = isDay(query.get('from'));
  const to = isDay(query.get('to'));
  const vesselId = query.get('vessel');
  const categoryId = query.get('type');
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; code: string }
    | { kind: 'ready'; data: DrillAnalyticsData }
  >({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({
        kind: 'ready',
        data: await source.load({ from, to, vesselId, categoryId }),
      });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, from, to, vesselId, categoryId]);
  useEffect(() => {
    void load();
  }, [load]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const data = state.kind === 'ready' ? state.data : null;
  const exportExcel = async () => {
    if (!data) return;
    setExporting(true);
    setExportError(null);
    try {
      const tx = (key: string, options?: Record<string, unknown>) => String(t(key, options ?? {}));
      const bytes = await analyticsWorkbook(data, tx);
      const name = analyticsFileName(data);
      if (source.saveFile) await source.saveFile(name, bytes, XLSX_TYPE);
      else downloadFile(name, bytes, XLSX_TYPE);
    } catch {
      setExportError(t('drillReport.archive.failed'));
    } finally {
      setExporting(false);
    }
  };
  const header = (
    <PageHeader
      title={t('menu.operationsDrillsAnalytics')}
      description={k('description')}
      actions={
        data ? (
          <Button
            variant="default"
            leftSection={<IconFileSpreadsheet size={16} aria-hidden />}
            loading={exporting}
            onClick={() => void exportExcel()}
          >
            {t('drillReport.actions.exportExcel')}
          </Button>
        ) : undefined
      }
    />
  );
  if (state.kind === 'loading') {
    return (
      <div className={table.screen}>
        {header}
        <TableSkeleton columns={9} label={t('menu.operationsDrillsAnalytics')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={table.screen}>
        {header}
        <Panel>
          <LoadError code={state.code} title={k('error')} onRetry={() => void load()} />
        </Panel>
      </div>
    );
  }
  return (
    <div className={table.screen}>
      {header}
      {exportError ? (
        <p role="alert" className={classes.error}>
          {exportError}
        </p>
      ) : null}
      <AnalyticsBody
        data={state.data}
        vesselId={vesselId}
        categoryId={categoryId}
        onFilter={(patch) => setQuery(patch)}
      />
    </div>
  );
}

/** Yüklenmiş analitik (ekran testleri doğrudan çizer). */
export function AnalyticsBody({
  data,
  vesselId = null,
  categoryId = null,
  onFilter,
}: {
  data: DrillAnalyticsData;
  vesselId?: string | null;
  categoryId?: string | null;
  onFilter?: (patch: Record<string, string | null>) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillReport.analytics.${key}`, values);
  const c = (key: string) => k(`columns.${key}`);
  const fleet = data.fleet;
  const rows: DrillVesselAnalytics[] = [
    ...data.vessels,
    ...(data.vessels.length > 1 ? [fleet] : []),
  ];
  return (
    <>
      <Panel>
        <div className={classes.filters}>
          <DayInput
            label={k('from')}
            value={data.range.from}
            onChange={(value) => onFilter?.({ from: value })}
            maxDate={data.range.to}
          />
          <DayInput
            label={k('to')}
            value={data.range.to}
            onChange={(value) => onFilter?.({ to: value })}
            minDate={data.range.from}
          />
          <Select
            label={k('vessel')}
            placeholder={k('allVessels')}
            clearable
            data={data.vesselOptions.map((v) => ({ value: v.id, label: v.name }))}
            value={vesselId}
            onChange={(value) => onFilter?.({ vessel: value })}
          />
          <Select
            label={k('type')}
            placeholder={k('allTypes')}
            clearable
            data={data.categories.map((cat) => ({ value: cat.id, label: cat.name }))}
            value={categoryId}
            onChange={(value) => onFilter?.({ type: value })}
          />
        </div>
        <p className={planner.muted}>
          {k('period', { from: formatDay(data.range.from), to: formatDay(data.range.to) })}
        </p>
      </Panel>
      <div className={planner.tiles} data-count="6">
        <StatTile
          icon={<IconChartPie size={20} />}
          label={k('kpi.completionRate')}
          value={fleet.completionRate.percent === null ? '—' : `${fleet.completionRate.percent}%`}
          tone={rateTone(fleet.completionRate.percent)}
          wrapLabel
        />
        <StatTile
          icon={<IconClockExclamation size={20} />}
          label={k('kpi.late')}
          value={fleet.late}
          tone={fleet.late > 0 ? 'warning' : 'default'}
          wrapLabel
        />
        <StatTile
          icon={<IconAlertTriangle size={20} />}
          label={k('kpi.overdue')}
          value={fleet.overdue}
          tone={fleet.overdue > 0 ? 'danger' : 'success'}
          wrapLabel
        />
        <StatTile
          icon={<IconUsersGroup size={20} />}
          label={k('kpi.muster')}
          value={time(fleet.musterAvgSeconds)}
          wrapLabel
        />
        <StatTile
          icon={<IconStopwatch size={20} />}
          label={k('kpi.response')}
          value={time(fleet.responseAvgSeconds)}
          wrapLabel
        />
        <StatTile
          icon={<IconFlag size={20} />}
          label={k('kpi.openFindings')}
          value={fleet.openFindings}
          tone={fleet.openFindings > 0 ? 'warning' : 'default'}
          wrapLabel
        />
      </div>
      <Panel title={k('comparison')} flush>
        <section
          className={table.tableBox}
          aria-label={k('comparison')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={table.table} data-cards data-testid="drill-analytics-vessels">
            <thead>
              <tr>
                <th scope="col">{c('vessel')}</th>
                <th scope="col">{c('planned')}</th>
                <th scope="col">{c('completed')}</th>
                <th scope="col">{c('completionRate')}</th>
                <th scope="col">{c('late')}</th>
                <th scope="col">{c('overdue')}</th>
                <th scope="col">{c('muster')}</th>
                <th scope="col">{c('response')}</th>
                <th scope="col">{c('openFindings')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.vesselId ?? 'fleet'} data-fleet={v.vesselId === null || undefined}>
                  <th scope="row" data-label={c('vessel')}>
                    {v.vesselName ?? k('fleet')}
                  </th>
                  <td data-label={c('planned')} data-numeric>
                    {v.planned}
                  </td>
                  <td data-label={c('completed')} data-numeric>
                    {v.completed}
                  </td>
                  <td data-label={c('completionRate')}>
                    <RateBar percent={v.completionRate.percent} />
                  </td>
                  <td data-label={c('late')} data-numeric>
                    {v.late}
                  </td>
                  <td data-label={c('overdue')} data-numeric>
                    {v.overdue}
                  </td>
                  <td data-label={c('muster')} data-numeric>
                    {time(v.musterAvgSeconds)}
                  </td>
                  <td data-label={c('response')} data-numeric>
                    {time(v.responseAvgSeconds)}
                  </td>
                  <td data-label={c('openFindings')} data-numeric>
                    {v.openFindings}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </Panel>
      <div className={classes.grid}>
        <Panel title={k('trend')}>
          <TrendChart data={data} />
        </Panel>
        <Panel title={k('types')}>
          <TypeBars data={data} />
        </Panel>
      </div>
      <p className={planner.muted}>{k('methodNote')}</p>
    </>
  );
}

function RateBar({ percent }: { percent: number | null }) {
  if (percent === null) return <span>—</span>;
  return (
    <span className={classes.rate}>
      <span className={classes.rateTrack} aria-hidden>
        <span
          className={classes.rateFill}
          data-tone={rateTone(percent)}
          style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
        />
      </span>
      <span className={classes.rateValue}>{`${percent}%`}</span>
    </span>
  );
}

const CHART_W = 640;
const CHART_H = 240;
const PAD = { left: 48, right: 16, top: 12, bottom: 30 };

/**
 * Aylık ortalama toplanma ya da tepki süresi, gemi başına çizgi (yakıt Genel Bakış grafiğinin
 * dili). Kaydı olmayan ay boşluk bırakır; noktanın ipucunda gemi, ay ve süre.
 */
function TrendChart({ data }: { data: DrillAnalyticsData }) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillReport.analytics.${key}`, values);
  const [metric, setMetric] = useState<'muster' | 'response'>('muster');
  const pick = (p: DrillVesselAnalytics['trend'][number]) =>
    metric === 'muster' ? p.musterAvgSeconds : p.responseAvgSeconds;
  const series = data.vessels.filter((v) => v.trend.some((p) => pick(p) !== null));
  const values = series.flatMap((v) => v.trend.map(pick).filter((x): x is number => x !== null));
  const top = axisMax(Math.max(0, ...values) / 60) * 60;
  const width = CHART_W - PAD.left - PAD.right;
  const height = CHART_H - PAD.top - PAD.bottom;
  const months = data.months;
  const x = (index: number) =>
    PAD.left + (months.length > 1 ? (index / (months.length - 1)) * width : width / 2);
  const y = (value: number) => PAD.top + height - (value / top) * height;
  const ticks = [0, top / 3, (2 * top) / 3, top];
  const monthLabel = (month: string) => `${month.slice(5, 7)}.${month.slice(2, 4)}`;
  const every = Math.ceil(months.length / 8);
  return (
    <figure className={classes.chart}>
      <div className={classes.chartHead}>
        <SegmentedControl
          size="xs"
          aria-label={k('metric')}
          value={metric}
          onChange={(value) => setMetric(value === 'response' ? 'response' : 'muster')}
          data={[
            { value: 'muster', label: k('metrics.muster') },
            { value: 'response', label: k('metrics.response') },
          ]}
        />
        <span className={classes.legend}>
          {series.map((v, index) => (
            <span className={classes.legendItem} key={v.vesselId}>
              <span className={classes.dot} data-series={index % 6} aria-hidden />
              {v.vesselName}
            </span>
          ))}
        </span>
      </div>
      {series.length === 0 ? (
        <EmptyState icon={<IconStopwatch size={22} />} title={k('noData')} />
      ) : (
        <svg
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          className={classes.chartSvg}
          role="img"
          aria-label={`${k('trend')}: ${k(`metrics.${metric}`)}`}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={CHART_W - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                className={classes.chartGrid}
              />
              <text x={PAD.left - 8} y={y(tick) + 4} className={classes.chartTick} textAnchor="end">
                {formatSeconds(Math.round(tick))}
              </text>
            </g>
          ))}
          {months.map((month, index) =>
            index % every === 0 || index === months.length - 1 ? (
              <text
                key={month}
                x={x(index)}
                y={CHART_H - 8}
                className={classes.chartTick}
                textAnchor="middle"
              >
                {monthLabel(month)}
              </text>
            ) : null,
          )}
          {series.map((v, index) => {
            const points = months.map((month) => {
              const point = v.trend.find((p) => p.month === month);
              return point ? pick(point) : null;
            });
            let path = '';
            points.forEach((value, i) => {
              if (value === null) return;
              path += `${i === 0 || points[i - 1] === null ? 'M' : 'L'}${x(i)},${y(value)} `;
            });
            return (
              <g key={v.vesselId} className={classes.chartSeries} data-series={index % 6}>
                <path d={path.trim()} className={classes.chartLine} />
                {points.map((value, i) =>
                  value === null ? null : (
                    <circle
                      key={months[i]}
                      cx={x(i)}
                      cy={y(value)}
                      r={4}
                      className={classes.chartPoint}
                    >
                      <title>
                        {k('trendPoint', {
                          vessel: v.vesselName,
                          month: monthLabel(months[i] ?? ''),
                          value: formatSeconds(value),
                        })}
                      </title>
                    </circle>
                  ),
                )}
              </g>
            );
          })}
        </svg>
      )}
      <figcaption className={planner.muted}>{k('trendHint')}</figcaption>
    </figure>
  );
}

/** Tatbikat türü dağılımı: yatay çubuk, kayıt sayısı ve pay. */
function TypeBars({ data }: { data: DrillAnalyticsData }) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillReport.analytics.${key}`, values);
  if (data.types.length === 0) {
    return <EmptyState icon={<IconChartPie size={22} />} title={k('noData')} />;
  }
  const max = Math.max(...data.types.map((s) => s.count));
  return (
    <>
      <ul className={classes.bars} aria-label={k('types')}>
        {data.types.map((share) => (
          <li key={share.categoryId ?? 'none'} className={classes.barRow}>
            <span className={classes.barLabel}>{share.categoryName ?? '—'}</span>
            <span className={classes.barTrack} aria-hidden>
              <span
                className={classes.barFill}
                style={{ width: `${max > 0 ? (share.count / max) * 100 : 0}%` }}
              />
            </span>
            <span className={classes.barValue}>{`${share.count} · ${share.percent}%`}</span>
          </li>
        ))}
      </ul>
      <p className={planner.muted}>{k('typesHint')}</p>
    </>
  );
}
